import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useUserStore } from '@/store/useUser';
import { complianceHide } from '@/utils/complianceHelpers';
import { SubMemberType } from '@/utils/constants/sub-account';

import useEscrowAccount from './useEscrowAccount';
import { useUserProfile } from './useUserProfile';

const useIslamicSubAccount = ({
  pageName,
}: {
  pageName: 'subAccount' | 'openApi';
}) => {
  const [search] = useSearchParams();
  const { complianceConfig } = useUserStore();
  const {
    isIslamicAccount: currentAccountIsIslamicAccount,
    grayFlag,
    userInfo,
  } = useUserProfile();
  const { institutionInfoCode } = useEscrowAccount();

  const hitIslamicGray = useMemo(() => {
    return grayFlag?.islan_gray;
  }, [grayFlag]); // 是否命中伊斯兰子账号灰度

  // 当前账号是否为伊斯兰子账号
  const isIslamicSubAccount = useMemo(() => {
    // 优先判断当前登录账号是否为伊斯兰子账号
    if (currentAccountIsIslamicAccount) {
      return true;
    }

    // 其次根据页面，各自判断是否为伊斯兰子账号

    // API管理页或者创建页面
    if (pageName === 'openApi') {
      const subAccountId =
        search.has('subaccount_id') || search.has('subAccountId'); // 创建api和编辑api传入的id字段不一样
      const memberType = search.get('memberType');
      return subAccountId
        ? memberType === SubMemberType.ISLAMIC_ACCOUNT
        : false;
    }

    return false;
  }, [search, currentAccountIsIslamicAccount, pageName]);

  // 是否允许创建伊斯兰子账号
  const allowCreateIslamicSubAccount = useMemo(() => {
    return (
      !complianceHide(complianceConfig?.IslamicSubAccountCreateEntry) && // 主站
      hitIslamicGray && // 未命中灰度
      !institutionInfoCode && // 未绑定过托管商的账户;
      !userInfo?.has_islamic // 未创建过伊斯兰子账号
    );
  }, [institutionInfoCode, hitIslamicGray, userInfo, complianceConfig]);

  return {
    isIslamicSubAccount,
    allowCreateIslamicSubAccount,
  };
};

export default useIslamicSubAccount;
